import classNames from 'classnames';
import React, {useMemo} from 'react';

import {GwgAccelerator} from '@udemy/career-academies';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';

import {CertificateShareLinks} from '../google-tracker/components/google-redirect-buttons.react-component';
import {LearningPathCourseStep} from './adapters/gwg-accelerator-to-learning-path';
import styles from './course-curriculums.module.less';
import {GoogleLearningPath} from './google-learning-path.react-component';
import {derivePathProgressState} from './learning-path-state';
import {PROGRESS_DESCRIPTIONS} from './utils/path-progress-descriptions';

export function CourseCurriculums({
    academyDescription,
    steps,
    isEnrolled,
    certificateTitle,
    certificateInfo,
    trackerCertificate,
}: {
    academyDescription: string;
    steps: LearningPathCourseStep[];
    isEnrolled: boolean;
    certificateTitle: string;
    certificateInfo?: GwgAccelerator['certificateInfo'] | null;
    trackerCertificate?: CertificateShareLinks;
}) {
    const isWiderThanMdMin = useMatchMedia('md-min');
    const {gettext, interpolate} = useI18n();
    const courseSeriesCount = steps.length;
    const hasCertificateInfo = !!certificateInfo;
    const pathTitle = hasCertificateInfo
        ? gettext('Certificate path – %(courseSeriesCount)s course series')
        : gettext('Learning path – %(courseSeriesCount)s course series');

    // The description above the cards is dynamic based on the user's status in the path.
    // Not enrolled keeps the path's own description; the enrolled states are path-independent.
    const description = useMemo(() => {
        const progressState = derivePathProgressState(steps, isEnrolled);
        const resolveDescription = PROGRESS_DESCRIPTIONS[progressState];

        // Fallback to default (NotEnrolled or undefined states)
        if (!resolveDescription) {
            return academyDescription;
        }

        return resolveDescription({gettext, interpolate}, certificateTitle);
    }, [academyDescription, certificateTitle, gettext, interpolate, steps, isEnrolled]);

    return (
        <div
            className={classNames({
                'ud-container': isWiderThanMdMin,
            })}
        >
            <div className={styles.header}>
                <div className={styles['icon-casing']}>
                    <Icon glyph="certificate" size="large" label={false} />
                </div>
                <h2 className={styles.title}>
                    {interpolate(pathTitle, {courseSeriesCount}, true)}
                </h2>
                {description && <p className={styles.description}>{description}</p>}
            </div>

            <GoogleLearningPath
                steps={steps}
                isAcceleratorEnrolled={isEnrolled}
                certificateTitle={certificateTitle}
                certificateInfo={certificateInfo}
                trackerCertificate={trackerCertificate}
            />
        </div>
    );
}
